import * as React from 'react';
import { Modal, Form, Input, Button, Popconfirm, Space } from 'antd';
import { DeleteOutlined, UploadOutlined } from '@ant-design/icons';
import { observer } from 'mobx-react-lite';
import { HomeShortcutsStore } from '../stores/HomeShortcutsStore';
import ImageUpload, { ImageUploadHandle } from './ImageUpload';
import type { HomeShortcutFormValues } from '../types/HomeShortcut';
import { endpoints } from '../../../config';

type Props = {
    store: HomeShortcutsStore;
};

const ShortcutEditModal: React.FC<Props> = ({ store }) => {
    const [form] = Form.useForm();
    const imageUploadRef = React.useRef<ImageUploadHandle>(null);
    const [selectedImage, setSelectedImage] = React.useState<File | null>(null);
    const [isImageDeleted, setIsImageDeleted] = React.useState(false);
    const [imageError, setImageError] = React.useState<string | undefined>(undefined);

    const editingShortcut = store.editingShortcut;
    const isEditing = !!editingShortcut;
    const currentPreviewUrl =
        editingShortcut?.imagePath && !isImageDeleted
            ? `${endpoints.imageUrl}?path=${encodeURIComponent(editingShortcut.imagePath)}`
            : undefined;
    const hasImage = !!selectedImage || !!currentPreviewUrl;
    React.useEffect(() => {
        if (!store.isEditModalVisible) {
            form.resetFields();
            setSelectedImage(null);
            setIsImageDeleted(false);
            setImageError(undefined);
            return;
        }

        form.setFieldsValue({
            title: editingShortcut?.title ?? '',
            description: editingShortcut?.description ?? '',
            url: editingShortcut?.url ?? ''
        });
        setSelectedImage(null);
        setIsImageDeleted(false);
        setImageError(undefined);
    }, [editingShortcut, form, store.isEditModalVisible]);

    const validateHttpsUrl = (_: unknown, value?: string) => {
        if (!value || !value.trim()) {
            return Promise.reject(new Error('Please enter a URL.'));
        }

        try {
            const url = new URL(value.trim());

            if (url.protocol !== 'https:') {
                return Promise.reject(new Error('URL must start with https://'));
            }

            return Promise.resolve();
        } catch {
            return Promise.reject(new Error('Please enter a valid absolute HTTPS URL.'));
        }
    };

    const handleClose = () => {
        store.closeEditModal();
        form.resetFields();
        setSelectedImage(null);
        setIsImageDeleted(false);
        setImageError(undefined);
    };

    const handleDeleteImage = () => {
        setSelectedImage(null);
        setIsImageDeleted(isEditing && !!editingShortcut?.imagePath);
        setImageError(undefined);
    };

    const handleTestLink = async () => {
        try {
            await form.validateFields(['url']);
            const url = (form.getFieldValue('url') as string).trim();
            window.open(url, '_blank', 'noopener,noreferrer');
        } catch {
            return;
        }
    };

    const handleSubmit = async () => {
        let values: HomeShortcutFormValues;

        try {
            values = await form.validateFields();
        } catch {
            return;
        }

        const payload: HomeShortcutFormValues = {
            title: values.title,
            description: values.description,
            url: values.url,
            image: selectedImage,
            deleteImage: isImageDeleted
        };

        setImageError(undefined);

        if (isEditing) {
            await store.updateShortcut(payload);
            return;
        }

        await store.createShortcut(payload);
    };

    return (
        <Modal
            className="alpha-portal-dialog"
            title={isEditing ? 'Edit shortcut' : 'Add shortcut'}
            visible={store.isEditModalVisible}
            onCancel={handleClose}
            maskClosable={false}
            closable={false}
            destroyOnClose
            centered
            footer={[
                <Button key="cancel" size="large" className="light" onClick={handleClose}>
                    Cancel
                </Button>,
                <Button key="test-link" size="large" onClick={handleTestLink} disabled={store.isSaving}>
                    Test link
                </Button>,
                <Button key="submit" type="primary" size="large" onClick={handleSubmit} loading={store.isSaving}>
                    {isEditing ? 'Save changes' : 'Create shortcut'}
                </Button>
            ]}
        >
            <Form form={form} layout="vertical">
                <Form.Item
                    label="Title"
                    name="title"
                    rules={[
                        { required: true, message: 'Please enter a title.' },
                        { max: 60, message: 'Title cannot exceed 60 characters.' }
                    ]}
                >
                    <Input maxLength={60} />
                </Form.Item>
                <Form.Item
                    label="Description"
                    name="description"
                    rules={[{ max: 150, message: 'Description cannot exceed 150 characters.' }]}
                >
                    <Input.TextArea rows={3} maxLength={150} />
                </Form.Item>
                <Form.Item label="URL" name="url" rules={[{ required: true, validator: validateHttpsUrl }]}>
                    <Input placeholder="https://example.com" />
                </Form.Item>
                <Form.Item
                    label="Image"
                    validateStatus={imageError ? 'error' : undefined}
                    help={imageError}
                    extra="Optional. JPEG, PNG, or WebP. Max 2 MB."
                >
                    <Space align="start">
                        <Form.Item noStyle shouldUpdate>
                            {({ getFieldValue }) => {
                                const deletedImagePlaceholderTitle = (
                                    (getFieldValue('title') as string | undefined) || ''
                                ).trim();

                                return (
                                    <ImageUpload
                                        ref={imageUploadRef}
                                        value={selectedImage}
                                        previewUrl={currentPreviewUrl}
                                        placeholder={
                                            isImageDeleted ? (
                                                <div className="shortcut-image-placeholder">
                                                    <span>{deletedImagePlaceholderTitle}</span>
                                                </div>
                                            ) : undefined
                                        }
                                        disabled={store.isSaving}
                                        onChange={file => {
                                            setSelectedImage(file);
                                            setIsImageDeleted(false);
                                            setImageError(undefined);
                                        }}
                                    />
                                );
                            }}
                        </Form.Item>
                        {hasImage ? (
                            <Space direction="vertical">
                                <Button
                                    icon={<UploadOutlined />}
                                    disabled={store.isSaving}
                                    onClick={() => imageUploadRef.current?.openFilePicker()}
                                >
                                    Replace
                                </Button>
                                <Popconfirm
                                    title="Are you sure you want to delete the image?"
                                    okText="Delete"
                                    cancelText="Cancel"
                                    okButtonProps={{ danger: true }}
                                    onConfirm={handleDeleteImage}
                                >
                                    <Button danger icon={<DeleteOutlined />} disabled={store.isSaving}>
                                        Delete
                                    </Button>
                                </Popconfirm>
                            </Space>
                        ) : null}
                    </Space>
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default observer(ShortcutEditModal);
